"use client";

import apis from "@/configs/apis";
import data from "@/public/data/landing.json";
import { Dialog, DialogContent, DialogTrigger } from "@/shared/components/common/Dialog";
import { useToast } from "@/shared/hooks/useToast";
import fetcher from "@/shared/libs/fetcher";
import { setCookie } from "@/shared/libs/utils";
import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";
import Confirm from "./components/Confirm";
import Login from "./components/Login";
import LoginWithPassword from "./components/LoginWithPassword";
import type { LoginWithPasswordResponse } from "@/types/services/public/apis/LoginWithPassword";
import type { ConfirmResponse } from "@/types/services/public/apis/Confirm";
import type { LoginResponse } from "@/types/services/public/apis/Login";
interface LoginSignupProps {
  children?: ReactNode;
}
export default function LoginSignup({
  children
}: LoginSignupProps) {
  const {
    header: {
      loginSignupModal: {
        confirm: {
          error: confirmError
        },
        loginWithPassword: {
          error: loginPassError
        }
      }
    }
  } = data;
  const [step, setStep] = useState<"login" | "confirm" | "loginWithPassword" | null>(null);
  const {
    toast
  } = useToast();
  const {
    isLoginModalOn,
    setIsLoginModalOn
  } = useGlobalStore(state => state);
  const router = useRouter();
  const {
    trigger: login,
    data: loginData,
    isMutating: isLogging
  } = useSWRMutation(apis.profile.login.client, async (url: string, {
    arg
  }: {
    arg: string;
  }) => await fetcher<LoginResponse>(url, {
    method: "POST",
    body: arg
  }));
  const {
    trigger: confirm,
    isMutating: isConfirming
  } = useSWRMutation(apis.profile.confirm.client, async (url: string, {
    arg
  }: {
    arg: string;
  }) => await fetcher<ConfirmResponse>(url, {
    method: "POST",
    body: arg
  }));
  const {
    trigger: loginWithPassword,
    isMutating: isLoggingWithPassword
  } = useSWRMutation(apis.profile.loginWithPassword.client, async (url: string, {
    arg
  }: {
    arg: string;
  }) => await fetcher<LoginWithPasswordResponse>(url, {
    method: "POST",
    body: arg
  }));
  const handleLogin = async (data: string) => {
    const {
      status,
      data: {
        succeed,
        message
      }
    } = await login(data);
    if (succeed) {
      setStep("confirm");
      toast({
        variant: "success",
        title: message
      });
    }
    if (!succeed) {
      if (status === 429) {
        setStep("confirm");
        toast({
          variant: "destructive",
          title: message
        });
      } else {
        toast({
          variant: "destructive",
          title: message
        });
      }
    }
  };
  const handleConfirm = async (data: string) => {
    const confirmResult = await confirm(data);
    const {
      succeed,
      message,
      access_token
    } = confirmResult.data;
    if (succeed) {
      setCookie("token", access_token, 7, undefined, undefined, true);
      toast({
        variant: "success",
        title: message
      });
      setStep(null);
      setIsLoginModalOn(false);
    }
    if (!succeed) {
      toast({
        variant: "destructive",
        title: confirmError.title,
        description: message
      });
    }
  };
  const handleLoginWithPassword = async (data: string) => {
    const loginWithPasswordResult = await loginWithPassword(data);
    const {
      succeed,
      message,
      access_token
    } = loginWithPasswordResult.data;
    if (succeed) {
      setCookie("token", access_token, 7, undefined, undefined, true);
      toast({
        variant: "success",
        title: message
      });
      setStep(null);
      setIsLoginModalOn(false);
    }
    if (!succeed) {
      toast({
        variant: "destructive",
        title: loginPassError.title,
        description: message
      });
    }
  };
  const handleCloseModal = () => {
    setIsLoginModalOn(false);
    setStep(null);
  };
  useEffect(() => {
    if (isLoginModalOn) setStep("login");
  }, [isLoginModalOn]);
  return <Dialog open={!!step} data-sentry-element="Dialog" data-sentry-component="LoginSignup" data-sentry-source-file="LoginSignup.tsx">
      <DialogTrigger asChild onClick={() => {
      setStep("login");
      setIsLoginModalOn(true);
    }} data-sentry-element="DialogTrigger" data-sentry-source-file="LoginSignup.tsx">
        {children}
      </DialogTrigger>
      <DialogContent className="max-sm:w-[95%] rounded-xs sm:w-[1000px] bg-background py-4" onClose={step === "confirm" ? () => setStep("login") : handleCloseModal} data-sentry-element="DialogContent" data-sentry-source-file="LoginSignup.tsx">
        {step === "login" ? <Login onSubmit={mobile => handleLogin(JSON.stringify({
        mobile: `0${+mobile}`
      }))} setStep={setStep} loading={isLogging} /> : step === "confirm" ? <Confirm isConfirming={isConfirming} mobile={loginData?.data?.results.mobile || ""} onChangeMobile={() => setStep("login")} resend={() => {
        const {
          mobile
        } = loginData?.data?.results ?? {};
        mobile && handleLogin(JSON.stringify({
          mobile: `0${+mobile}`
        }));
      }} confirm={async confirmCode => {
        const {
          mobile
        } = loginData?.data.results ?? {};
        if (mobile) {
          await handleConfirm(JSON.stringify({
            mobile,
            confirm_code: confirmCode
          }));
          router.refresh();
        }
      }} /> : <LoginWithPassword loading={isLoggingWithPassword} setStep={setStep} onSubmit={async data => {
        await handleLoginWithPassword(JSON.stringify({
          mobile: `0${+data.mobile}`,
          password: data.password
        }));
        router.refresh();
      }} />}
      </DialogContent>
    </Dialog>;
}